import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  NotificationChannel,
  NotificationEventKey,
} from "@colab/shared";
import type { NotificationPreferenceCell } from "./use-notification-preferences";

// PATCH /api/portal/preferences
//
// optimistic: toggle flip is the user-visible reflex; defer DB confirmation.
// dispatchEvent does NOT yet honor these toggles — see migration 0070 +
// MIGRATION.md sub-bucket-7 entry.
//
// Pattern C (state-flip) — see MIGRATION.md "Optimistic-update reference".
// Same shape as use-toggle-partner-task.

export type UpdateNotificationPreferenceInput = {
  event_key: NotificationEventKey;
  channel: NotificationChannel;
  enabled: boolean;
};

type Context = {
  previous: NotificationPreferenceCell[] | undefined;
};

export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    UpdateNotificationPreferenceInput,
    Context
  >({
    mutationFn: (input) =>
      apiFetch<{ ok: true }>("/portal/preferences", {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onMutate: async (input) => {
      const key = queryKeys.portal.preferences();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<NotificationPreferenceCell[]>(key);
      queryClient.setQueryData<NotificationPreferenceCell[]>(key, (old) =>
        old?.map((cell) =>
          cell.event_key === input.event_key && cell.channel === input.channel
            ? { ...cell, enabled: input.enabled }
            : cell,
        ),
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.portal.preferences(), context.previous);
      }
      toast.error(err.message ?? "Failed to update preference");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.preferences() });
    },
  });
}
